/**
 * /downhole/templates — reusable BHA patterns (fieldiq-api#882 reads,
 * #883 `POST /templates`), full width. A row opens the template on its own
 * page, /downhole/templates/:templateId (TemplateDetailScreen), where Build,
 * Archive and Restore live. No panel opens beside the list: split panes are
 * banned (CLAUDE.md). A legacy `?id=<id>` link is forwarded to that page.
 *
 * A TEMPLATE IS SLOTS: slot number, category, label, required, notes. That is
 * all `dh_assembly_template_slots` holds. The unrouted form posted
 * `component_types` with an OD and a connection per row and no `bha_type`, so
 * every create was a 400, and the two per-slot fields it collected have no
 * column to land in. Both are gone; `bha_type` is required.
 *
 * A template has no edit route, so the form says so before the reader saves.
 * A duplicate name is the API's 409, shown as it is.
 *
 * ARCHIVE, NOT DELETE (api#902). `DELETE /templates/:id` sets is_active false
 * and `POST /templates/:id/restore` sets it back; assemblies built from it
 * keep the pointer. An archived template stays in this list with an Archived
 * pill — the list is the register — but it cannot seed a build (the API
 * answers 409), so "Build an assembly from this" is not offered on it, and the
 * builder's own picker asks for `?active=true`.
 */
import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useCreateTemplate, useDownholeTemplates } from '../../hooks/useDownhole';
import { unwrapArray } from '../../lib/api-envelope';
import { fmtQty } from '../../lib/display-labels';
import { DH_BHA_TYPE_LABEL, DH_CATEGORY_LABEL, dhLabel } from '../../lib/downhole-labels';
import { dhUnitLabel, formatDh, toStoredImperial } from '../../lib/downhole-units';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_BHA_TYPES, DH_COMPONENT_CATEGORIES, type DhUnitSystem } from '../../types/downhole';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel } from './DownholeBits';
import { useOpenRecord } from './recordPage';

/** Archived is a fact the reader needs before building; slate, so it claims no status hue. */
export function ArchivedPill() {
  return (
    <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink">
      Archived
    </span>
  );
}

interface SlotDraft {
  key: number;
  category: string;
  label: string;
  required: boolean;
  notes: string;
}

function NewTemplateDialog({ open, onClose, units }: { open: boolean; onClose: () => void; units: DhUnitSystem | null }) {
  const create = useCreateTemplate();
  const navigate = useNavigate();
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const [name, setName] = useState('');
  const [bhaType, setBhaType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [holeSize, setHoleSize] = useState('');
  const [wellType, setWellType] = useState('');
  const [formation, setFormation] = useState('');
  const [description, setDescription] = useState('');
  const [slots, setSlots] = useState<SlotDraft[]>([{ key: 1, category: '', label: '', required: true, notes: '' }]);
  const [nextKey, setNextKey] = useState(2);

  const reset = () => {
    setName('');
    setBhaType('');
    setCustomerId('');
    setHoleSize('');
    setWellType('');
    setFormation('');
    setDescription('');
    setSlots([{ key: 1, category: '', label: '', required: true, notes: '' }]);
  };

  const setSlot = (key: number, patch: Partial<SlotDraft>) => setSlots((s) => s.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const submit = async () => {
    if (!name.trim() || !bhaType) {
      toast.error('A template needs a name and a BHA type.');
      return;
    }
    if (slots.length === 0 || slots.some((s) => !s.category)) {
      toast.error('Every slot needs a category.');
      return;
    }
    if (holeSize.trim() && toStoredImperial(holeSize, 'diameter', units, 3) === null) {
      toast.error('Hole size must be a number.');
      return;
    }
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        bha_type: bhaType,
        description: description.trim() || null,
        customer_id: customerId || null,
        hole_size: toStoredImperial(holeSize, 'diameter', units, 3),
        well_type: wellType.trim() || null,
        formation: formation.trim() || null,
        slots: slots.map((s, i) => ({
          slot_number: i + 1,
          category: s.category,
          label: s.label.trim() || null,
          required: s.required,
          notes: s.notes.trim() || null,
        })),
      });
      toast.success(`Template “${name.trim()}” saved`);
      reset();
      onClose();
      // The new template opens on its own page; its back arrow returns here.
      if (result.data) navigate(`/downhole/templates/${result.data.id}`, { state: { listUrl: '/downhole/templates' } });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The template was not saved.'));
    }
  };

  return (
    <DhDialog
      open={open}
      wide
      title="New template"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50" onClick={submit} disabled={create.isPending}>
            Save template
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <DhField label="Name *">
            <input className={DH_INPUT} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
          </DhField>
          <DhField label="BHA type *">
            <select className={DH_INPUT} value={bhaType} onChange={(e) => setBhaType(e.target.value)}>
              <option value="">Choose…</option>
              {DH_BHA_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DH_BHA_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </DhField>
          <DhField label="Customer">
            <select className={DH_INPUT} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">Any customer</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </DhField>
          <DhField label={`Hole size (${dhUnitLabel('diameter', units)})`}>
            <input className={DH_INPUT} type="number" inputMode="decimal" min={0} step="any" value={holeSize} onChange={(e) => setHoleSize(e.target.value)} />
          </DhField>
          <DhField label="Well type">
            <input className={DH_INPUT} value={wellType} onChange={(e) => setWellType(e.target.value)} maxLength={30} />
          </DhField>
          <DhField label="Formation">
            <input className={DH_INPUT} value={formation} onChange={(e) => setFormation(e.target.value)} maxLength={100} />
          </DhField>
          <div className="sm:col-span-2">
            <DhField label="Description">
              <textarea className={`${DH_INPUT} h-20 py-2`} value={description} onChange={(e) => setDescription(e.target.value)} />
            </DhField>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Slots, top to bottom *</legend>
          {slots.map((s, i) => (
            <div key={s.key} className="grid items-center gap-2 rounded-fi-control border border-fieldiq-border p-2 sm:grid-cols-[2rem_1fr_1fr_auto_auto]">
              <span className="font-mono text-sm text-fieldiq-muted">{i + 1}</span>
              <select className={DH_INPUT} aria-label={`Slot ${i + 1} category`} value={s.category} onChange={(e) => setSlot(s.key, { category: e.target.value })}>
                <option value="">Category…</option>
                {DH_COMPONENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {DH_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
              <input
                className={DH_INPUT}
                aria-label={`Slot ${i + 1} label`}
                placeholder="Label (optional)"
                value={s.label}
                maxLength={100}
                onChange={(e) => setSlot(s.key, { label: e.target.value })}
              />
              <label className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                <input type="checkbox" checked={s.required} onChange={(e) => setSlot(s.key, { required: e.target.checked })} />
                Required
              </label>
              <button
                type="button"
                className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-white disabled:opacity-30"
                aria-label={`Remove slot ${i + 1}`}
                disabled={slots.length === 1}
                onClick={() => setSlots((all) => all.filter((x) => x.key !== s.key))}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={DH_SECONDARY}
            onClick={() => {
              setSlots((all) => [...all, { key: nextKey, category: '', label: '', required: true, notes: '' }]);
              setNextKey((k) => k + 1);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Add slot
          </button>
        </fieldset>
        <p className="text-xs text-fieldiq-muted">Templates cannot be edited or deleted once saved.</p>
      </div>
    </DhDialog>
  );
}

export default function TemplatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams] = useSearchParams();
  const legacyId = searchParams.get('id');
  const [creating, setCreating] = useState(false);
  const query = useDownholeTemplates();
  const failure = queryLoadFailure(query);
  const templates = query.data?.data ?? [];
  const units = query.data?.meta.unit_system ?? null;

  const open = (id: string) => openRecord(`/downhole/templates/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  if (legacyId) return <Navigate to={`/downhole/templates/${legacyId}`} replace />;

  return (
    <div data-testid="downhole-templates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Templates"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => setCreating(true)} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" />
            New template
          </button>
        }
        /* No stat strip. The register below is the whole set and shows every
           count a strip would; a two-stat strip is also geometry the band's
           circle clearance was never measured against. */
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel
            eyebrow="BHA patterns"
            title="Templates"
            testId="downhole-template-register"
            footer={!query.isLoading && !failure && templates.length > 0 ? `${templates.length} ${templates.length === 1 ? 'template' : 'templates'}` : undefined}
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading templates" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="templates" />
            ) : templates.length === 0 ? (
              <DhEmpty>There are no templates yet.</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[760px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr>
                        <th scope="col" className="px-4 py-3">Name</th>
                        <th scope="col" className="px-4 py-3">BHA type</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3 text-right">Hole size</th>
                        <th scope="col" className="px-4 py-3 text-right">Slots</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((t) => (
                        <tr
                          key={t.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(t.id)}
                          onKeyDown={rowKeys(t.id)}
                          className="cursor-pointer border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="px-4 py-3">
                            <p className="flex items-center gap-2 text-sm font-extrabold text-white">
                              {t.name}
                              {!t.is_active && <ArchivedPill />}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{dhLabel(DH_BHA_TYPE_LABEL, t.bha_type)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{displayValue(t.customer_name)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(t.hole_size, 'diameter', units)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtQty(t.slot_count)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={rowKeys(t.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex items-center gap-2 text-sm font-extrabold text-white">
                        {t.name}
                        {!t.is_active && <ArchivedPill />}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {dhLabel(DH_BHA_TYPE_LABEL, t.bha_type)} · {displayValue(t.customer_name)}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-text">
                        <span className="font-mono">{formatDh(t.hole_size, 'diameter', units)}</span> · {fmtQty(t.slot_count)} slots
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      <NewTemplateDialog open={creating} onClose={() => setCreating(false)} units={units} />
    </div>
  );
}
